'use client'

import { useEffect, useMemo, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import Link from 'next/link'

import { formatCents, formatCentsRange, multiplierSummaryLine, visibleBreakdownItems } from '@/lib/calculator/format'

// Post-capture breakdown reveal (Task 4). Renders the snapshot the submit
// route (T5) returns — the ONLY place worthCents ever reaches the client,
// by design (engine.ts's publicConfig() strips it everywhere upstream of
// capture). Each item animates in, worth anchors strike through when
// present, and the anchored total leads into the CTA pair.
const CONFETTI_COLORS = ['#C9A84C', '#d9bd65', '#34d399', '#f5f5f3']
const CONFETTI_COUNT = 36

function groupBySection(items, config) {
  const titleMap = new Map()
  for (const section of config?.sections ?? []) {
    if (section?.key) titleMap.set(section.key, section.title || section.key)
  }
  const order = []
  const bySection = new Map()
  for (const item of items) {
    if (!bySection.has(item.sectionKey)) {
      bySection.set(item.sectionKey, [])
      order.push(item.sectionKey)
    }
    bySection.get(item.sectionKey).push(item)
  }
  return order
    .map((key) => ({ key, title: titleMap.get(key) || key, items: bySection.get(key) }))
    .filter((section) => section.items.length > 0) // every item in this section was decluttered — drop the empty section header too
}

function ConfettiBurst() {
  const dots = useMemo(
    () =>
      Array.from({ length: CONFETTI_COUNT }, () => ({
        left: Math.random() * 100,
        delay: Math.random() * 300,
        duration: 900 + Math.random() * 700,
        drift: (Math.random() - 0.5) * 90,
        size: 3 + Math.floor(Math.random() * 3),
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        round: Math.random() > 0.4,
      })),
    [],
  )

  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      {dots.map((d, i) => (
        <span
          key={i}
          style={{
            left: `${d.left}%`,
            width: d.size,
            height: d.size,
            background: d.color,
            borderRadius: d.round ? '9999px' : '1px',
            animation: `calc-confetti-fall ${d.duration}ms linear ${d.delay}ms forwards`,
            '--calc-drift': `${d.drift}px`,
          }}
          className="absolute -top-2"
        />
      ))}
      <style>{`
        @keyframes calc-confetti-fall {
          0% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
          100% { transform: translate(var(--calc-drift), 140px) rotate(360deg); opacity: 0; }
        }
      `}</style>
    </div>
  )
}

export function BreakdownStack({ config, snapshot, resultsToken }) {
  const shouldReduceMotion = useReducedMotion()
  const allItems = useMemo(() => (Array.isArray(snapshot?.items) ? snapshot.items : []), [snapshot])
  // Declutter "nothing extra to build here" answers (zero-cost, no explicit
  // breakdownLabel) before grouping — see visibleBreakdownItems' doc comment.
  const items = useMemo(() => visibleBreakdownItems(allItems), [allItems])
  const grouped = useMemo(() => groupBySection(items, config), [items, config])
  const multiplierLine = useMemo(
    () => multiplierSummaryLine(allItems, snapshot?.appliedMultiplierPct ?? 100),
    [allItems, snapshot],
  )

  // Confetti nod — consistent with the house pixel language (see the
  // Zero-to-Hero CrownCeremony), scaled down to a scoped burst rather than
  // a full-screen takeover, and skipped entirely under reduced motion.
  const [confettiOn, setConfettiOn] = useState(false)
  useEffect(() => {
    if (shouldReduceMotion) return
    setConfettiOn(true)
    const timer = setTimeout(() => setConfettiOn(false), 2000)
    return () => clearTimeout(timer)
  }, [shouldReduceMotion])

  return (
    <div data-testid="calc-breakdown" data-results-token={resultsToken || undefined}>
      <div className="text-center">
        <p className="text-xs font-semibold tracking-[0.2em] text-emerald-400 uppercase">Unlocked</p>
        <h2 className="mt-3 font-display text-xl font-medium text-white sm:text-2xl">
          {config?.resultsHeadline || 'Your itemized breakdown'}
        </h2>
      </div>

      <div className="mt-7 space-y-6">
        {grouped.map((section) => (
          <div key={section.key}>
            <p className="text-[0.65rem] font-semibold tracking-widest text-white/40 uppercase">
              {section.title}
            </p>
            <div className="mt-2 divide-y divide-white/5">
              {section.items.map((item, i) => (
                <motion.div
                  key={`${item.questionKey}-${item.label}-${i}`}
                  data-testid="calc-breakdown-item"
                  initial={shouldReduceMotion ? false : { opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: shouldReduceMotion ? 0 : 0.35,
                    delay: shouldReduceMotion ? 0 : i * 0.06,
                  }}
                  className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-2.5"
                >
                  <span className="text-sm text-white/80">{item.label}</span>
                  {/* Zero-range items only render at all when they carry an
                      explicit breakdownLabel (visibleBreakdownItems) — the
                      label itself already says what that means (e.g.
                      "— included"), so skip the redundant "included: $0"
                      suffix rather than print a bare $0 figure. */}
                  {!(item.minCents === 0 && item.maxCents === 0) && (
                    <span className="text-right text-sm">
                      {typeof item.worthCents === 'number' && item.worthCents > 0 && (
                        <span className="mr-2 text-white/35 line-through" data-testid="calc-breakdown-worth">
                          worth {formatCents(item.worthCents)}
                        </span>
                      )}
                      <span className="font-semibold text-kingdom-gold">
                        included: {formatCentsRange(item.minCents, item.maxCents)}
                      </span>
                    </span>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {multiplierLine && (
        <p className="mt-4 text-center text-xs font-medium text-white/50" data-testid="calc-breakdown-multiplier">
          {multiplierLine}
        </p>
      )}

      <motion.div
        className="relative mt-8 overflow-hidden rounded-2xl border border-kingdom-gold/30 bg-kingdom-gold/10 p-6 text-center"
        initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.4,
          delay: shouldReduceMotion ? 0 : grouped.length * 0.06,
        }}
        data-testid="calc-breakdown-total"
      >
        {confettiOn && <ConfettiBurst />}
        <p className="text-xs font-semibold tracking-widest text-kingdom-gold uppercase">Your total</p>
        <p className="mt-2 font-display text-3xl font-semibold text-white">
          {formatCentsRange(snapshot?.totalMinCents ?? 0, snapshot?.totalMaxCents ?? 0)}
        </p>
      </motion.div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <Link
          href="/contact"
          data-testid="calc-cta-book-call"
          className="min-h-11 flex-1 rounded-full bg-kingdom-gold px-6 py-3 text-center text-sm font-semibold text-neutral-950 transition hover:bg-[#d9bd65]"
        >
          Book a strategy call
        </Link>
        <Link
          href="/diagnostic"
          data-testid="calc-cta-diagnostic"
          className="min-h-11 flex-1 rounded-full border border-white/15 px-6 py-3 text-center text-sm font-medium text-neutral-300 transition hover:border-white/40"
        >
          Run the free diagnostic
        </Link>
      </div>
    </div>
  )
}
